import {
  BarChart,
  Bar,
  Rectangle,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { Box, Card, SxProps, Theme } from "@mui/material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { useTheme } from "@mui/material";

type BarChartLayout = "vertical" | "horizontal";

export interface AppBarChartProps {
  title: string;
  sx?: SxProps<Theme>;
  data: any[];
  dataKey: string;
  data_keys_config: AppChartKeyConfig[];
  layout: BarChartLayout;
  barCategoryGap?: number;
  barSize?: number;
  barGap?: number;
  fontSize?: string;
  legendFontSize?: string;
}

export interface AppChartKeyConfig {
  key: string;
  color: string;
  activeColor: string;
}

export default function AppBarChart({
  title,
  sx,
  layout,
  data,
  dataKey,
  data_keys_config,
  barSize = 10,
  barCategoryGap,
  barGap = 5,
  fontSize = "0.8rem",
  legendFontSize = "0.8rem",
}: AppBarChartProps) {
  const theme = useTheme();

  function getLayout(layout: BarChartLayout): BarChartLayout {
    if (layout === "horizontal") return "vertical";
    else return "horizontal";
  }

  return (
    <Card sx={sx}>
      <CustomTypography variant="heading4">{title}</CustomTypography>
      {data.length > 0 && (
        <ResponsiveContainer>
          <BarChart
            data={data}
            layout={getLayout(layout)}
            margin={{
              top: 5,
              right: 30,
              left: 30,
              bottom: 20,
            }}
            barCategoryGap={barCategoryGap}
            barSize={barSize}
            barGap={barGap}
          >
            <Legend
              verticalAlign="top"
              align="right"
              wrapperStyle={{ paddingBottom: "1rem", fontSize: legendFontSize }}
            />

            {layout === "vertical" ? (
              <>
                <CartesianGrid
                  strokeDasharray="3 3"
                  horizontal={true}
                  vertical={false}
                />
                <XAxis dataKey={dataKey} type="category" fontSize={fontSize} />
                <YAxis
                  type="number"
                  fontSize={fontSize}
                  tickFormatter={(value) => `${value}`}
                />
              </>
            ) : (
              <>
                <CartesianGrid
                  strokeDasharray="3 3"
                  horizontal={false}
                  vertical={true}
                />
                <XAxis
                  type="number"
                  fontSize={fontSize}
                  tickFormatter={(value) => `${value}`}
                />

                <YAxis dataKey={dataKey} type="category" fontSize={fontSize} />
              </>
            )}

            <Tooltip
              contentStyle={{ fontSize: fontSize }}
              itemStyle={{ fontSize: fontSize }}
            />

            {data_keys_config.map((key_config) => (
              <Bar
                radius={layout === "vertical" ? [2, 2, 0, 0] : [0, 2, 2, 0]}
                dataKey={key_config.key}
                fill={key_config.color}
                activeBar={
                  <Rectangle
                    fill={key_config.activeColor}
                    stroke={key_config.color}
                  />
                }
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
      {data.length === 0 && (
        <Box
          sx={{
            width: "100%",
            height: "80%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CustomTypography variant="heading8" color="textSecondary">
            Nothing to show for the selected filters
          </CustomTypography>
        </Box>
      )}
    </Card>
  );
}
